import {
  type AutomationAction,
  type AutomationTrigger,
  type CannedVisibility,
  type Condition,
  type ConditionRule,
  type FieldAccess,
  type FieldConfig,
  FieldConfigSchema,
  type FieldType,
  type FilterNode,
  type FormKind,
  type FormLayout,
  type GranteeType,
  type IssueTypePortal,
  IssueTypePortalSchema,
  isFieldKey,
  newFieldKey,
  newId,
  PAGE_SIZE_DEFAULT,
  type PermissionKey,
  type PostFunction,
  type PriorityUrgency,
  type ProjectSettings,
  ProjectSettingsSchema,
  type ProjectTemplateDocument,
  type SearchRequest,
  type SlaCalendar,
  type SlaGoals,
  type Status,
  type StatusCategory,
  SYSTEM_FIELDS,
  SYSTEM_PROJECT_ROLES,
  TEMPLATE_VERSION,
  type TemplateAutomationRule,
  type TemplateBinding,
  type TemplateConditionLeaf,
  type TemplateConditionRule,
  type TemplateField,
  type TemplateFilterNode,
  type TemplateForm,
  type TemplateFormField,
  type TemplateFormLayout,
  type TemplateIssueType,
  type TemplatePermissionGrant,
  type TemplatePostFunction,
  type TemplatePriority,
  type TemplateSearchRequest,
  type TemplateSlaPolicy,
  type TemplateStatus,
  type TemplateTransition,
  type TemplateValidator,
  type TemplateView,
  type TemplateWorkflow,
  type Transition,
  type Validator,
  type ViewKind,
  type WorkflowSnapshot,
} from '@servicetim/shared';

/*
 * Template normalizer (ADR-0013 §7, aidlc/domain/project-template.md).
 *
 * `materializeTemplate(doc, ctx)` turns a portable document into insert-ready rows with
 * generated ids and `cf_` keys; `exportTemplate(rows)` turns rows back into a document with
 * local refs. Both are pure. The row shapes below are structural mirrors of the Drizzle
 * insert types (this package must not import Drizzle), so the API and the seed can pass them
 * straight to `tx.insert(...)`.
 */

/* ----------------------------- row shapes ----------------------------- */

export interface RoleRowT {
  id: string;
  tenantId: string;
  projectId: string;
  key: string;
  name: string;
  isSystem: boolean;
}
export interface GrantRowT {
  id: string;
  tenantId: string;
  schemeId: string;
  permission: PermissionKey;
  granteeType: GranteeType;
  granteeId: string | null;
}
export interface WorkflowRowT {
  id: string;
  tenantId: string;
  projectId: string;
  name: string;
  publishedVersionId: string | null;
  draftDirty: boolean;
}
export interface WorkflowVersionRowT {
  id: string;
  tenantId: string;
  workflowId: string;
  version: number;
  snapshot: WorkflowSnapshot;
  statusMapping: Record<string, string> | null;
  publishedBy: string | null;
}
export interface StatusRowT {
  id: string;
  tenantId: string;
  workflowId: string;
  key: string;
  name: string;
  category: StatusCategory;
  portalName: string | null;
  color: string | null;
  position: number;
  isInitial: boolean;
  archivedAt?: Date | null;
}
export interface TransitionRowT {
  id: string;
  tenantId: string;
  workflowId: string;
  key: string;
  name: string;
  fromStatusIds: string[] | null;
  toStatusId: string;
  isInitial: boolean;
  formId: string | null;
  conditions: ConditionRule;
  validators: Validator[];
  postFunctions: PostFunction[];
  position: number;
}
export interface PriorityRowT {
  id: string;
  tenantId: string;
  projectId: string;
  name: string;
  color: string | null;
  rank: number;
  urgency: PriorityUrgency;
  isDefault: boolean;
}
export interface FieldRowT {
  id: string;
  tenantId: string;
  key: string;
  scope: 'project' | 'tenant';
  projectId: string | null;
  type: FieldType;
  label: string;
  description: string | null;
  config: FieldConfig;
  indexed: boolean;
  enforceUnique: boolean;
  schemaVersion: number;
  archivedAt?: Date | null;
}
export interface OptionRowT {
  id: string;
  tenantId: string;
  fieldId: string;
  label: string;
  color: string | null;
  position: number;
  archivedAt?: Date | null;
}
export interface BindingRowT {
  id: string;
  tenantId: string;
  projectId: string;
  fieldId: string;
  required: boolean;
  defaultValue: unknown;
  position: number;
  access: FieldAccess | null;
  issueTypeIds: string[] | null;
}
export interface IssueTypeRowT {
  id: string;
  tenantId: string;
  projectId: string;
  name: string;
  description: string | null;
  icon: string | null;
  workflowId: string;
  isSubtask: boolean;
  position: number;
  portal: IssueTypePortal;
}
export interface FormRowT {
  id: string;
  tenantId: string;
  projectId: string;
  issueTypeId: string | null;
  kind: FormKind;
  name: string;
  layout: FormLayout;
}
export interface ViewRowT {
  id: string;
  tenantId: string;
  projectId: string | null;
  ownerId: string;
  name: string;
  kind: ViewKind;
  query: SearchRequest;
  shared: boolean;
  portalVisible: boolean;
  position: number;
}
export interface SlaPolicyRowT {
  id: string;
  tenantId: string;
  projectId: string;
  name: string;
  goals: SlaGoals;
  startConditions: ConditionRule[];
  pauseConditions: ConditionRule[];
  stopConditions: ConditionRule[];
  calendar: SlaCalendar | null;
  enabled: boolean;
}
export interface CannedResponseRowT {
  id: string;
  tenantId: string;
  projectId: string | null;
  name: string;
  body: string;
  visibility: CannedVisibility;
  position: number;
}
export interface AutomationRuleRowT {
  id: string;
  tenantId: string;
  projectId: string;
  name: string;
  description: string | null;
  enabled: boolean;
  isSystem: boolean;
  trigger: AutomationTrigger;
  conditions: ConditionRule[];
  actions: AutomationAction[];
  position: number;
  createdBy: string | null;
}

/** Insert plan produced by `materializeTemplate`; also a valid `ExportInput`. */
export interface TemplateRows {
  project: { name: string | null; category: string | null; settings: ProjectSettings };
  roles: RoleRowT[];
  grants: GrantRowT[];
  workflows: WorkflowRowT[];
  statuses: StatusRowT[];
  transitions: TransitionRowT[];
  workflowVersions: WorkflowVersionRowT[];
  priorities: PriorityRowT[];
  fields: FieldRowT[];
  options: OptionRowT[];
  bindings: BindingRowT[];
  issueTypes: IssueTypeRowT[];
  forms: FormRowT[];
  views: ViewRowT[];
  slaPolicies: SlaPolicyRowT[];
  cannedResponses: CannedResponseRowT[];
  automations: AutomationRuleRowT[];
}

export interface MaterializeContext {
  tenantId: string;
  projectId: string;
  /** Scheme the grants are written to (created by the caller, in the same transaction). */
  permissionSchemeId: string;
  /** Owner of the seeded views and publisher of the workflow versions. */
  ownerId: string;
  /** Id generator; defaults to `newId()`. Injectable for deterministic tests. */
  ids?: () => string;
  /** Custom-field key generator; defaults to `newFieldKey()`. */
  fieldKeys?: () => string;
}

/* ----------------------------- shared helpers ----------------------------- */

const SYSTEM_ROLE_NAMES: Record<(typeof SYSTEM_PROJECT_ROLES)[number], string> = {
  project_admin: 'Project admin',
  agent: 'Agent',
  customer: 'Customer',
};

const SELECT_TYPES: ReadonlySet<FieldType> = new Set<FieldType>(['single_select', 'multi_select']);

const withValue = <T extends object>(base: T, value: unknown): T & { value?: unknown } =>
  value === undefined ? base : { ...base, value };

const withMessage = <T extends object>(base: T, message: string | undefined) =>
  message === undefined ? base : { ...base, message };

/** Maps select values (option ref ↔ option id) through `map`; other values pass through. */
function mapSelectValue(value: unknown, map: ReadonlyMap<string, string> | undefined): unknown {
  if (!map) {
    return value;
  }
  if (typeof value === 'string') {
    return map.get(value) ?? value;
  }
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'string' ? (map.get(v) ?? v) : v));
  }
  return value;
}

/** Full config → the sparse form templates carry: keys that differ from the type defaults. */
export function minimalFieldConfig(config: FieldConfig): Record<string, unknown> {
  const defaults = FieldConfigSchema.parse({ type: config.type }) as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(config as Record<string, unknown>)) {
    if (key !== 'type' && canonicalJson(value) !== canonicalJson(defaults[key])) {
      out[key] = value;
    }
  }
  return out;
}

/* ----------------------------- import (materialize) ----------------------------- */

interface ImportField {
  key: string;
  type: FieldType;
  optionIds: Map<string, string>;
}

interface ImportRefs {
  roleId: (key: string) => string;
  field: (ref: string) => ImportField;
}

function importLeaf(
  leaf: { field?: string; fieldRef?: string; value?: unknown },
  refs: ImportRefs,
): { field: string; value?: unknown } {
  if (leaf.fieldRef !== undefined) {
    const f = refs.field(leaf.fieldRef);
    return withValue(
      { field: f.key },
      SELECT_TYPES.has(f.type) ? mapSelectValue(leaf.value, f.optionIds) : leaf.value,
    );
  }
  return withValue({ field: leaf.field as string }, leaf.value);
}

function importCondition(rule: TemplateConditionRule, refs: ImportRefs): ConditionRule {
  if ('all' in rule) {
    return { all: rule.all.map((r) => importCondition(r, refs)) };
  }
  if ('any' in rule) {
    return { any: rule.any.map((r) => importCondition(r, refs)) };
  }
  if ('not' in rule) {
    return { not: importCondition(rule.not, refs) };
  }
  switch (rule.type) {
    case 'has_permission':
      return { type: 'has_permission', permission: rule.permission };
    case 'user_is':
      return { type: 'user_is', who: rule.who };
    case 'user_in_role':
      return { type: 'user_in_role', roleId: refs.roleId(rule.roleKey) };
    case 'field_matches': {
      const leaf = importLeaf(rule, refs);
      return withValue({ type: 'field_matches', field: leaf.field, op: rule.op }, leaf.value);
    }
  }
}

function importValidator(v: TemplateValidator, refs: ImportRefs): Validator {
  switch (v.type) {
    case 'field_required':
      return withMessage({ type: 'field_required', field: importLeaf(v, refs).field }, v.message);
    case 'field_matches': {
      const leaf = importLeaf(v, refs);
      return withMessage(
        withValue({ type: 'field_matches', field: leaf.field, op: v.op }, leaf.value),
        v.message,
      );
    }
    case 'no_open_children':
      return withMessage({ type: 'no_open_children' as const }, v.message);
    case 'approval_required':
      return withMessage(
        {
          type: 'approval_required' as const,
          approvers: { kind: 'role' as const, roleId: refs.roleId(v.approverRoleKey) },
          mode: 'all' as const,
        },
        v.message,
      );
  }
}

function importPostFunction(p: TemplatePostFunction, refs: ImportRefs): PostFunction {
  switch (p.type) {
    case 'set_field': {
      const leaf = importLeaf(p, refs);
      return { type: 'set_field', field: leaf.field, value: leaf.value };
    }
    case 'assign':
      return { type: 'assign', to: p.to };
    case 'emit_event':
      return { type: 'emit_event', eventType: p.eventType };
    case 'add_comment':
      return { type: 'add_comment', body: p.body, visibility: p.visibility };
  }
}

function importFilter(node: TemplateFilterNode, refs: ImportRefs): FilterNode {
  if ('and' in node) {
    return { and: node.and.map((n) => importFilter(n, refs)) };
  }
  if ('or' in node) {
    return { or: node.or.map((n) => importFilter(n, refs)) };
  }
  if ('not' in node) {
    return { not: importFilter(node.not, refs) };
  }
  const leaf = importLeaf(node, refs);
  return withValue({ field: leaf.field, op: node.op }, leaf.value) as Condition;
}

function importQuery(q: TemplateSearchRequest, refs: ImportRefs): SearchRequest {
  const fieldOf = (s: { field?: string; fieldRef?: string }): string =>
    s.fieldRef !== undefined ? refs.field(s.fieldRef).key : (s.field as string);
  const out: SearchRequest = {
    sort: q.sort.map((s) => ({ field: fieldOf(s), dir: s.dir })),
    page: { size: PAGE_SIZE_DEFAULT },
  };
  if (q.filter) {
    out.filter = importFilter(q.filter, refs);
  }
  if (q.groupBy) {
    out.groupBy = { field: fieldOf(q.groupBy), limitPerGroup: q.groupBy.limitPerGroup };
  }
  if (q.select) {
    out.select = q.select.map((s) => (typeof s === 'string' ? s : refs.field(s.fieldRef).key));
  }
  return out;
}

function importLayout(layout: TemplateFormLayout, refs: ImportRefs): FormLayout {
  return {
    sections: layout.sections.map((section) => ({
      ...(section.title !== undefined ? { title: section.title } : {}),
      ...(section.description !== undefined ? { description: section.description } : {}),
      fields: section.fields.map((f: TemplateFormField) => {
        const out: FormLayout['sections'][number]['fields'][number] = {
          key: 'fieldRef' in f ? refs.field(f.fieldRef).key : f.key,
        };
        if (f.label !== undefined) {
          out.label = f.label;
        }
        if (f.required !== undefined) {
          out.required = f.required;
        }
        if (f.readOnly !== undefined) {
          out.readOnly = f.readOnly;
        }
        if (f.visibleWhen) {
          const leaf = importLeaf(f.visibleWhen, refs);
          out.visibleWhen = withValue({ field: leaf.field, op: f.visibleWhen.op }, leaf.value);
        }
        return out;
      }),
    })),
  };
}

/**
 * Builds the insert plan for a template. Every `ref` becomes a generated UUIDv7, every field
 * gets a fresh `cf_` key, roles are resolved by key (missing system roles are added), each
 * workflow is published as version 1 and select values are rewritten to option ids. The
 * plan lists rows per table; `insertTemplateRows` in `@servicetim/db` writes them in FK order.
 */
export function materializeTemplate(
  doc: ProjectTemplateDocument,
  ctx: MaterializeContext,
): TemplateRows {
  const id = ctx.ids ?? newId;
  const fieldKey = ctx.fieldKeys ?? newFieldKey;
  const { tenantId, projectId } = ctx;

  // Roles: template order first, then any system role the template did not declare.
  const roles: RoleRowT[] = doc.roles.map((r) => ({
    id: id(),
    tenantId,
    projectId,
    key: r.key,
    name: r.name,
    isSystem: (SYSTEM_PROJECT_ROLES as readonly string[]).includes(r.key),
  }));
  for (const key of SYSTEM_PROJECT_ROLES) {
    if (!roles.some((r) => r.key === key)) {
      roles.push({
        id: id(),
        tenantId,
        projectId,
        key,
        name: SYSTEM_ROLE_NAMES[key],
        isSystem: true,
      });
    }
  }
  const roleIdByKey = new Map(roles.map((r) => [r.key, r.id]));
  const roleId = (key: string): string => {
    const found = roleIdByKey.get(key);
    if (!found) {
      throw new Error(`template.dangling_ref: role ${key}`);
    }
    return found;
  };

  const grants: GrantRowT[] = doc.permissionGrants
    .filter((g) => g.granteeType !== 'user' && g.granteeType !== 'group')
    .map((g: TemplatePermissionGrant) => ({
      id: id(),
      tenantId,
      schemeId: ctx.permissionSchemeId,
      permission: g.permission,
      granteeType: g.granteeType,
      granteeId:
        g.granteeType === 'project_role'
          ? roleId(g.granteeRoleKey as string)
          : g.granteeType === 'tenant_role'
            ? (g.granteeTenantRole as string)
            : null,
    }));

  // Fields and options.
  const fields: FieldRowT[] = [];
  const options: OptionRowT[] = [];
  const fieldByRef = new Map<string, ImportField>();
  for (const f of doc.fields) {
    const fieldId = id();
    const key = fieldKey();
    fields.push({
      id: fieldId,
      tenantId,
      key,
      scope: 'project',
      projectId,
      type: f.type,
      label: f.label,
      description: f.description,
      config: FieldConfigSchema.parse({ ...f.config, type: f.type }),
      indexed: f.indexed,
      enforceUnique: f.enforceUnique,
      schemaVersion: 1,
    });
    const optionIds = new Map<string, string>();
    (f.options ?? []).forEach((o, position) => {
      const optionId = id();
      optionIds.set(o.ref, optionId);
      options.push({
        id: optionId,
        tenantId,
        fieldId,
        label: o.label,
        color: o.color,
        position,
      });
    });
    fieldByRef.set(f.ref, { key, type: f.type, optionIds });
  }
  const refs: ImportRefs = {
    roleId,
    field: (ref) => {
      const f = fieldByRef.get(ref);
      if (!f) {
        throw new Error(`template.dangling_ref: field ${ref}`);
      }
      return f;
    },
  };

  // Workflows, statuses, transitions, published version 1.
  const workflows: WorkflowRowT[] = [];
  const statuses: StatusRowT[] = [];
  const transitions: TransitionRowT[] = [];
  const workflowVersions: WorkflowVersionRowT[] = [];
  const workflowIdByRef = new Map<string, string>();
  for (const w of doc.workflows) {
    const workflowId = id();
    const versionId = id();
    workflowIdByRef.set(w.ref, workflowId);
    const statusIdByRef = new Map<string, string>();
    const wfStatuses: StatusRowT[] = w.statuses.map((s: TemplateStatus) => {
      const statusId = id();
      statusIdByRef.set(s.ref, statusId);
      return {
        id: statusId,
        tenantId,
        workflowId,
        key: s.key,
        name: s.name,
        category: s.category,
        portalName: s.portalName,
        color: s.color,
        position: s.position,
        isInitial: s.ref === w.initialStatusRef,
      };
    });
    const statusId = (ref: string): string => {
      const found = statusIdByRef.get(ref);
      if (!found) {
        throw new Error(`template.dangling_ref: status ${ref}`);
      }
      return found;
    };
    const wfTransitions: TransitionRowT[] = w.transitions.map((t: TemplateTransition) => ({
      id: id(),
      tenantId,
      workflowId,
      key: t.key,
      name: t.name,
      fromStatusIds: t.fromStatusRefs === null ? null : t.fromStatusRefs.map(statusId),
      toStatusId: statusId(t.toStatusRef),
      isInitial: t.isInitial,
      formId: null,
      conditions: importCondition(t.conditions, refs),
      validators: t.validators.map((v) => importValidator(v, refs)),
      postFunctions: t.postFunctions.map((p) => importPostFunction(p, refs)),
      position: t.position,
    }));
    statuses.push(...wfStatuses);
    transitions.push(...wfTransitions);
    workflows.push({
      id: workflowId,
      tenantId,
      projectId,
      name: w.name,
      publishedVersionId: versionId,
      draftDirty: false,
    });
    workflowVersions.push({
      id: versionId,
      tenantId,
      workflowId,
      version: 1,
      snapshot: snapshotOf(1, statusId(w.initialStatusRef), wfStatuses, wfTransitions),
      statusMapping: null,
      publishedBy: ctx.ownerId,
    });
  }
  const workflowId = (ref: string): string => {
    const found = workflowIdByRef.get(ref);
    if (!found) {
      throw new Error(`template.dangling_ref: workflow ${ref}`);
    }
    return found;
  };

  // Priorities: exactly one default (the first one when the template flags none).
  const hasDefault = doc.priorities.some((p) => p.isDefault);
  const priorities: PriorityRowT[] = doc.priorities.map((p: TemplatePriority, i) => ({
    id: id(),
    tenantId,
    projectId,
    name: p.name,
    color: p.color,
    rank: p.rank,
    urgency: p.urgency,
    isDefault: hasDefault ? p.isDefault : i === 0,
  }));
  const priorityIdByName = new Map(priorities.map((p) => [p.name, p.id]));

  // Issue types.
  const issueTypes: IssueTypeRowT[] = doc.issueTypes.map((t: TemplateIssueType) => ({
    id: id(),
    tenantId,
    projectId,
    name: t.name,
    description: t.description,
    icon: t.icon,
    workflowId: workflowId(t.workflowRef),
    isSubtask: t.isSubtask,
    position: t.position,
    portal: IssueTypePortalSchema.parse(t.portal),
  }));
  const issueTypeIdByRef = new Map(doc.issueTypes.map((t, i) => [t.ref, issueTypes[i]?.id ?? '']));
  const issueTypeId = (ref: string): string => {
    const found = issueTypeIdByRef.get(ref);
    if (!found) {
      throw new Error(`template.dangling_ref: issue type ${ref}`);
    }
    return found;
  };

  // Bindings.
  const bindings: BindingRowT[] = doc.bindings.map((b: TemplateBinding) => {
    const f = refs.field(b.fieldRef);
    const fieldId = fields.find((row) => row.key === f.key)?.id as string;
    return {
      id: id(),
      tenantId,
      projectId,
      fieldId,
      required: b.required,
      defaultValue:
        b.defaultValue === undefined
          ? null
          : SELECT_TYPES.has(f.type)
            ? mapSelectValue(b.defaultValue, f.optionIds)
            : b.defaultValue,
      position: b.position,
      access: b.access
        ? {
            readRoles: b.access.readRoles === null ? null : b.access.readRoles.map(roleId),
            editRoles: b.access.editRoles === null ? null : b.access.editRoles.map(roleId),
          }
        : null,
      issueTypeIds: b.issueTypeRefs === null ? null : b.issueTypeRefs.map(issueTypeId),
    };
  });

  const forms: FormRowT[] = doc.forms.map((f: TemplateForm) => ({
    id: id(),
    tenantId,
    projectId,
    issueTypeId: f.issueTypeRef === null ? null : issueTypeId(f.issueTypeRef),
    kind: f.kind,
    name: f.name,
    layout: importLayout(f.layout, refs),
  }));

  const views: ViewRowT[] = doc.views.map((v: TemplateView) => ({
    id: id(),
    tenantId,
    projectId,
    ownerId: ctx.ownerId,
    name: v.name,
    kind: v.kind,
    query: importQuery(v.query, refs),
    shared: v.shared,
    portalVisible: v.portalVisible,
    position: v.position,
  }));

  const slaPolicies: SlaPolicyRowT[] = doc.slaPolicies.map((s: TemplateSlaPolicy) => {
    const goals: SlaGoals = {};
    for (const [name, goal] of Object.entries(s.goals)) {
      const pid = priorityIdByName.get(name);
      if (!pid) {
        throw new Error(`template.dangling_ref: priority ${name}`);
      }
      goals[pid] = goal;
    }
    return {
      id: id(),
      tenantId,
      projectId,
      name: s.name,
      goals,
      startConditions: s.startConditions.map((c) => importCondition(c, refs)),
      pauseConditions: s.pauseConditions.map((c) => importCondition(c, refs)),
      stopConditions: s.stopConditions.map((c) => importCondition(c, refs)),
      calendar: s.calendar,
      enabled: s.enabled,
    };
  });

  const cannedResponses: CannedResponseRowT[] = doc.cannedResponses.map((c) => ({
    id: id(),
    tenantId,
    projectId,
    name: c.name,
    body: c.body,
    visibility: c.visibility,
    position: c.position,
  }));

  // Automations (S-07-11): no ref indirection — the trigger/conditions/actions a template
  // carries are the real grammar already (see `TemplateAutomationRuleSchema`), so this is a
  // straight copy plus generated ids; `createdBy` becomes the seeding owner so actions that
  // need an author (`add_comment`, `create_ticket`) work even though a template rule has no
  // real creator of its own.
  const automations: AutomationRuleRowT[] = doc.automations.map((a: TemplateAutomationRule) => ({
    id: id(),
    tenantId,
    projectId,
    name: a.name,
    description: a.description,
    enabled: a.enabled,
    isSystem: a.isSystem,
    trigger: a.trigger,
    conditions: a.conditions,
    actions: a.actions,
    position: a.position,
    createdBy: ctx.ownerId,
  }));

  return {
    project: {
      name: doc.project.name ?? null,
      category: doc.project.category ?? null,
      settings: ProjectSettingsSchema.parse(doc.project.settings ?? {}),
    },
    roles,
    grants,
    workflows,
    statuses,
    transitions,
    workflowVersions,
    priorities,
    fields,
    options,
    bindings,
    issueTypes,
    forms,
    views,
    slaPolicies,
    cannedResponses,
    automations,
  };
}

/** Builds the immutable snapshot stored in `workflow_version` from draft rows. */
export function snapshotOf(
  version: number,
  initialStatusId: string,
  statuses: readonly Pick<
    StatusRowT,
    'id' | 'key' | 'name' | 'category' | 'color' | 'position' | 'portalName'
  >[],
  transitions: readonly Pick<
    TransitionRowT,
    | 'id'
    | 'key'
    | 'name'
    | 'fromStatusIds'
    | 'toStatusId'
    | 'conditions'
    | 'validators'
    | 'postFunctions'
    | 'formId'
    | 'position'
  >[],
): WorkflowSnapshot {
  const byPosition = <T extends { position: number; id: string }>(rows: readonly T[]): T[] =>
    [...rows].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
  return {
    version,
    initialStatusId,
    statuses: byPosition(statuses).map(
      (s): Status => ({
        id: s.id,
        key: s.key,
        name: s.name,
        category: s.category,
        color: s.color,
        position: s.position,
        portalName: s.portalName,
      }),
    ),
    transitions: byPosition(transitions).map(
      (t): Transition => ({
        id: t.id,
        key: t.key,
        name: t.name,
        fromStatusIds: t.fromStatusIds,
        toStatusId: t.toStatusId,
        conditions: t.conditions,
        validators: t.validators,
        postFunctions: t.postFunctions,
        formId: t.formId,
        position: t.position,
      }),
    ),
  };
}

/* ----------------------------- export ----------------------------- */

/** What `exportTemplate` needs; Drizzle select rows satisfy these shapes structurally. */
export interface ExportInput {
  project: { name: string | null; category: string | null; settings: ProjectSettings };
  /** Roles of the project (listed in the document). */
  roles: readonly Pick<RoleRowT, 'id' | 'key' | 'name'>[];
  /** Tenant-wide roles, used only to translate ids in grants and bindings. */
  extraRoles?: readonly Pick<RoleRowT, 'id' | 'key'>[];
  grants: readonly Pick<GrantRowT, 'id' | 'permission' | 'granteeType' | 'granteeId'>[];
  workflows: readonly Pick<WorkflowRowT, 'id' | 'name'>[];
  statuses: readonly Omit<StatusRowT, 'tenantId'>[];
  transitions: readonly Omit<TransitionRowT, 'tenantId'>[];
  priorities: readonly Omit<PriorityRowT, 'tenantId' | 'projectId'>[];
  fields: readonly Omit<FieldRowT, 'tenantId' | 'scope' | 'projectId' | 'schemaVersion'>[];
  options: readonly Omit<OptionRowT, 'tenantId'>[];
  bindings: readonly Omit<BindingRowT, 'tenantId' | 'projectId'>[];
  issueTypes: readonly Omit<IssueTypeRowT, 'tenantId' | 'projectId'>[];
  forms: readonly Omit<FormRowT, 'tenantId' | 'projectId'>[];
  views: readonly Omit<ViewRowT, 'tenantId' | 'projectId' | 'ownerId'>[];
  slaPolicies: readonly Omit<SlaPolicyRowT, 'tenantId' | 'projectId'>[];
  cannedResponses: readonly Omit<CannedResponseRowT, 'tenantId' | 'projectId'>[];
  automations: readonly Omit<AutomationRuleRowT, 'tenantId' | 'projectId' | 'createdBy'>[];
}

interface ExportField {
  ref: string;
  type: FieldType;
  optionRefs: Map<string, string>;
}

interface ExportRefs {
  roleKey: (id: string) => string | undefined;
  field: (key: string) => ExportField | undefined;
}

const SYSTEM_FIELD_NAMES: ReadonlySet<string> = new Set<string>(SYSTEM_FIELDS);

/** Lowercase slug matching `TEMPLATE_REF_PATTERN`, at most 36 chars to leave room for a suffix. */
export function slugify(input: string): string {
  const base = input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 36)
    .replace(/_+$/, '');
  if (base === '') {
    return 'x';
  }
  return /^[a-z]/.test(base) ? base : `x_${base}`.slice(0, 36);
}

/** `slug`, `slug_2`, `slug_3`… never colliding with `taken`; records the pick. */
export function uniqueRef(base: string, taken: Set<string>): string {
  let candidate = base;
  for (let n = 2; taken.has(candidate); n += 1) {
    candidate = `${base}_${n}`;
  }
  taken.add(candidate);
  return candidate;
}

const byId = <T extends { id: string }>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => a.id.localeCompare(b.id));
const byPosition = <T extends { id: string; position: number }>(rows: readonly T[]): T[] =>
  [...rows].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
const active = <T extends { archivedAt?: Date | null }>(rows: readonly T[]): T[] =>
  rows.filter((r) => r.archivedAt == null);

type Leaf = { field: string; fieldRef?: never } | { fieldRef: string; field?: never };

/** `cf_` key → `{ fieldRef }`, system name → `{ field }`; undefined when the field is not exportable. */
function exportLeaf(
  field: string,
  value: unknown,
  refs: ExportRefs,
): (Leaf & { value?: unknown }) | undefined {
  if (isFieldKey(field)) {
    const f = refs.field(field);
    if (!f) {
      return undefined;
    }
    return withValue(
      { fieldRef: f.ref },
      SELECT_TYPES.has(f.type) ? mapSelectValue(value, f.optionRefs) : value,
    );
  }
  if (!SYSTEM_FIELD_NAMES.has(field)) {
    return undefined;
  }
  return withValue({ field }, value);
}

/** Drops non-portable rules (`user_in_group`, unknown fields); empty `any` collapses to always. */
function exportCondition(rule: ConditionRule, refs: ExportRefs): TemplateConditionRule | undefined {
  if ('all' in rule) {
    return {
      all: rule.all.map((r) => exportCondition(r, refs)).filter((r) => r !== undefined),
    };
  }
  if ('any' in rule) {
    const any = rule.any.map((r) => exportCondition(r, refs)).filter((r) => r !== undefined);
    return any.length > 0 ? { any } : undefined;
  }
  if ('not' in rule) {
    const inner = exportCondition(rule.not, refs);
    return inner ? { not: inner } : undefined;
  }
  switch (rule.type) {
    case 'has_permission':
      return { type: 'has_permission', permission: rule.permission };
    case 'user_is':
      return { type: 'user_is', who: rule.who };
    case 'user_in_role': {
      const roleKey = refs.roleKey(rule.roleId);
      return roleKey ? { type: 'user_in_role', roleKey } : undefined;
    }
    case 'user_in_group':
      return undefined;
    case 'field_matches': {
      const leaf = exportLeaf(rule.field, rule.value, refs);
      return leaf
        ? ({ type: 'field_matches', op: rule.op, ...leaf } as TemplateConditionRule)
        : undefined;
    }
  }
}

function exportValidator(v: Validator, refs: ExportRefs): TemplateValidator | undefined {
  switch (v.type) {
    case 'field_required': {
      const leaf = exportLeaf(v.field, undefined, refs);
      return leaf
        ? (withMessage({ type: 'field_required', ...leaf }, v.message) as TemplateValidator)
        : undefined;
    }
    case 'field_matches': {
      const leaf = exportLeaf(v.field, v.value, refs);
      return leaf
        ? (withMessage(
            { type: 'field_matches', op: v.op, ...leaf },
            v.message,
          ) as TemplateValidator)
        : undefined;
    }
    case 'no_open_children':
      return withMessage({ type: 'no_open_children' as const }, v.message);
    case 'approval_required': {
      // Only role-based approvers are portable in v1 templates; user/group/field specs are
      // tenant-local and are dropped (the transition is exported without this validator).
      if (v.approvers.kind !== 'role') {
        return undefined;
      }
      const approverRoleKey = refs.roleKey(v.approvers.roleId);
      return approverRoleKey
        ? withMessage({ type: 'approval_required' as const, approverRoleKey }, v.message)
        : undefined;
    }
  }
}

function exportPostFunction(p: PostFunction, refs: ExportRefs): TemplatePostFunction | undefined {
  switch (p.type) {
    case 'set_field': {
      const leaf = exportLeaf(p.field, p.value, refs);
      return leaf
        ? ({ type: 'set_field', ...leaf, value: leaf.value } as TemplatePostFunction)
        : undefined;
    }
    case 'assign':
      return typeof p.to === 'string' ? { type: 'assign', to: p.to } : undefined;
    case 'emit_event':
      return { type: 'emit_event', eventType: p.eventType };
    case 'add_comment':
      return { type: 'add_comment', body: p.body, visibility: p.visibility };
  }
}

function exportFilter(node: FilterNode, refs: ExportRefs): TemplateFilterNode | undefined {
  if ('and' in node) {
    const and = node.and.map((n) => exportFilter(n, refs)).filter((n) => n !== undefined);
    return and.length > 0 ? { and } : undefined;
  }
  if ('or' in node) {
    const or = node.or.map((n) => exportFilter(n, refs)).filter((n) => n !== undefined);
    return or.length > 0 ? { or } : undefined;
  }
  if ('not' in node) {
    const inner = exportFilter(node.not, refs);
    return inner ? { not: inner } : undefined;
  }
  const leaf = exportLeaf(node.field, node.value, refs);
  return leaf ? ({ op: node.op, ...leaf } as TemplateFilterNode) : undefined;
}

function exportQuery(q: SearchRequest, refs: ExportRefs): TemplateSearchRequest {
  const out: TemplateSearchRequest = { sort: [] };
  if (q.filter) {
    const filter = exportFilter(q.filter, refs);
    if (filter) {
      out.filter = filter;
    }
  }
  for (const s of q.sort) {
    const leaf = exportLeaf(s.field, undefined, refs);
    if (leaf) {
      out.sort.push({ ...leaf, dir: s.dir } as TemplateSearchRequest['sort'][number]);
    }
  }
  if (q.groupBy) {
    const leaf = exportLeaf(q.groupBy.field, undefined, refs);
    if (leaf) {
      out.groupBy = {
        ...leaf,
        limitPerGroup: q.groupBy.limitPerGroup,
      } as NonNullable<TemplateSearchRequest['groupBy']>;
    }
  }
  if (q.select) {
    out.select = q.select
      .map((s) => exportLeaf(s, undefined, refs))
      .filter((l) => l !== undefined)
      .map((l) =>
        'fieldRef' in l && l.fieldRef !== undefined ? { fieldRef: l.fieldRef } : l.field,
      ) as NonNullable<TemplateSearchRequest['select']>;
  }
  return out;
}

function exportLayout(layout: FormLayout, refs: ExportRefs): TemplateFormLayout {
  return {
    sections: layout.sections.map((section) => ({
      ...(section.title !== undefined ? { title: section.title } : {}),
      ...(section.description !== undefined ? { description: section.description } : {}),
      fields: section.fields
        .map((f): TemplateFormField | undefined => {
          const leaf = exportLeaf(f.key, undefined, refs);
          if (!leaf) {
            return undefined;
          }
          const target: Record<string, unknown> =
            'fieldRef' in leaf && leaf.fieldRef !== undefined
              ? { fieldRef: leaf.fieldRef }
              : { key: leaf.field };
          if (f.label !== undefined) {
            target.label = f.label;
          }
          if (f.required !== undefined) {
            target.required = f.required;
          }
          if (f.readOnly !== undefined) {
            target.readOnly = f.readOnly;
          }
          if (f.visibleWhen) {
            const cond = exportLeaf(f.visibleWhen.field, f.visibleWhen.value, refs);
            if (cond) {
              target.visibleWhen = { op: f.visibleWhen.op, ...cond } as TemplateConditionLeaf;
            }
          }
          return target as TemplateFormField;
        })
        .filter((f) => f !== undefined),
    })),
  };
}

/**
 * Turns configuration rows into a portable document: ids become refs (field refs from a slug
 * of the label, option refs from a slug of the option label, status refs from status keys,
 * workflow and issue-type refs from a slug of the name, roles by key, priorities by name),
 * archived statuses/fields/options are skipped, and anything tenant-specific (`user`/`group`
 * grants, `user_in_group` conditions, `assign` to a user id) is dropped. Ordering is by
 * `position` where the entity has one and by id (creation order) otherwise, so
 * `exportTemplate(materializeTemplate(T))` reproduces `T`.
 */
export function exportTemplate(input: ExportInput): ProjectTemplateDocument {
  const roleKeyById = new Map<string, string>();
  for (const r of [...input.roles, ...(input.extraRoles ?? [])]) {
    roleKeyById.set(r.id, r.key);
  }

  // Fields: only active fields that are bound to the project.
  const activeFields = new Map(active(input.fields).map((f) => [f.id, f]));
  const boundFieldIds = new Set(input.bindings.map((b) => b.fieldId));
  const exportedFields = byId([...activeFields.values()].filter((f) => boundFieldIds.has(f.id)));
  const fieldRefs = new Set<string>(SYSTEM_FIELD_NAMES);
  const fieldByKey = new Map<string, ExportField>();
  const fieldById = new Map<string, ExportField>();
  const optionsByField = new Map<string, OptionRowT[]>();
  for (const o of byPosition(active(input.options))) {
    const list = optionsByField.get(o.fieldId) ?? [];
    list.push(o as OptionRowT);
    optionsByField.set(o.fieldId, list);
  }
  const fieldDocs: TemplateField[] = exportedFields.map((f) => {
    const ref = uniqueRef(slugify(f.label), fieldRefs);
    const optionRefs = new Map<string, string>();
    const taken = new Set<string>();
    const options = (optionsByField.get(f.id) ?? []).map((o) => {
      const oref = uniqueRef(slugify(o.label), taken);
      optionRefs.set(o.id, oref);
      return { ref: oref, label: o.label, color: o.color };
    });
    const exported: ExportField = { ref, type: f.type, optionRefs };
    fieldByKey.set(f.key, exported);
    fieldById.set(f.id, exported);
    const doc: TemplateField = {
      ref,
      type: f.type,
      label: f.label,
      description: f.description,
      config: minimalFieldConfig(f.config),
      indexed: f.indexed,
      enforceUnique: f.enforceUnique,
    };
    if (SELECT_TYPES.has(f.type)) {
      doc.options = options;
    }
    return doc;
  });
  const refs: ExportRefs = {
    roleKey: (id) => roleKeyById.get(id),
    field: (key) => fieldByKey.get(key),
  };

  // Workflows.
  const workflowRefs = new Set<string>();
  const workflowRefById = new Map<string, string>();
  const workflowDocs: TemplateWorkflow[] = byId(input.workflows).map((w) => {
    const ref = uniqueRef(slugify(w.name), workflowRefs);
    workflowRefById.set(w.id, ref);
    const statuses = byPosition(active(input.statuses.filter((s) => s.workflowId === w.id)));
    const statusRefById = new Map(statuses.map((s) => [s.id, s.key]));
    const initial = statuses.find((s) => s.isInitial) ?? statuses[0];
    // Archived sources are dropped from `fromStatusIds`; a transition whose target is archived
    // or whose sources all vanished (while it had some) is dead and left out.
    const transitions = byPosition(input.transitions.filter((t) => t.workflowId === w.id))
      .map((t) => ({
        ...t,
        fromStatusIds:
          t.fromStatusIds === null ? null : t.fromStatusIds.filter((s) => statusRefById.has(s)),
      }))
      .filter(
        (t) =>
          statusRefById.has(t.toStatusId) &&
          (t.fromStatusIds === null ||
            t.fromStatusIds.length > 0 ||
            (input.transitions.find((x) => x.id === t.id)?.fromStatusIds?.length ?? 0) === 0),
      )
      .map(
        (t): TemplateTransition => ({
          key: t.key,
          name: t.name,
          fromStatusRefs:
            t.fromStatusIds === null
              ? null
              : t.fromStatusIds.map((s) => statusRefById.get(s) as string),
          toStatusRef: statusRefById.get(t.toStatusId) as string,
          isInitial: t.isInitial,
          conditions: exportCondition(t.conditions, refs) ?? { all: [] },
          validators: t.validators
            .map((v) => exportValidator(v, refs))
            .filter((v) => v !== undefined),
          postFunctions: t.postFunctions
            .map((p) => exportPostFunction(p, refs))
            .filter((p) => p !== undefined),
          position: t.position,
        }),
      );
    return {
      ref,
      name: w.name,
      initialStatusRef: initial?.key ?? '',
      statuses: statuses.map(
        (s): TemplateStatus => ({
          ref: s.key,
          key: s.key,
          name: s.name,
          category: s.category,
          color: s.color,
          position: s.position,
          portalName: s.portalName,
        }),
      ),
      transitions,
    };
  });

  // Issue types.
  const issueTypeRefs = new Set<string>();
  const issueTypeRefById = new Map<string, string>();
  const issueTypeDocs: TemplateIssueType[] = byPosition(input.issueTypes)
    .filter((t) => workflowRefById.has(t.workflowId))
    .map((t) => {
      const ref = uniqueRef(slugify(t.name), issueTypeRefs);
      issueTypeRefById.set(t.id, ref);
      return {
        ref,
        name: t.name,
        description: t.description,
        icon: t.icon,
        workflowRef: workflowRefById.get(t.workflowId) as string,
        isSubtask: t.isSubtask,
        position: t.position,
        portal: IssueTypePortalSchema.parse(t.portal),
      };
    });

  const priorityDocs: TemplatePriority[] = [...input.priorities]
    .sort((a, b) => a.rank - b.rank || a.id.localeCompare(b.id))
    .map((p) => ({
      name: p.name,
      color: p.color,
      rank: p.rank,
      urgency: p.urgency,
      isDefault: p.isDefault,
    }));
  const priorityNameById = new Map(input.priorities.map((p) => [p.id, p.name]));

  const roleKeys = (ids: string[] | null): string[] | null =>
    ids === null ? null : ids.map((id) => roleKeyById.get(id)).filter((k) => k !== undefined);

  const bindingDocs: TemplateBinding[] = byPosition(input.bindings)
    .filter((b) => fieldById.has(b.fieldId))
    .map((b) => {
      const f = fieldById.get(b.fieldId) as ExportField;
      const doc: TemplateBinding = {
        fieldRef: f.ref,
        required: b.required,
        position: b.position,
        access: b.access
          ? { readRoles: roleKeys(b.access.readRoles), editRoles: roleKeys(b.access.editRoles) }
          : null,
        issueTypeRefs:
          b.issueTypeIds === null
            ? null
            : b.issueTypeIds.map((id) => issueTypeRefById.get(id)).filter((r) => r !== undefined),
      };
      if (b.defaultValue !== null && b.defaultValue !== undefined) {
        doc.defaultValue = SELECT_TYPES.has(f.type)
          ? mapSelectValue(b.defaultValue, f.optionRefs)
          : b.defaultValue;
      }
      return doc;
    });

  const formDocs: TemplateForm[] = byId(input.forms)
    .filter((f) => f.issueTypeId === null || issueTypeRefById.has(f.issueTypeId))
    .map((f) => ({
      kind: f.kind,
      issueTypeRef: f.issueTypeId === null ? null : (issueTypeRefById.get(f.issueTypeId) as string),
      name: f.name,
      layout: exportLayout(f.layout, refs),
    }));

  const roleDocs = byId(input.roles).map((r) => ({ key: r.key, name: r.name }));

  const grantDocs: TemplatePermissionGrant[] = byId(input.grants)
    .map((g): TemplatePermissionGrant | undefined => {
      switch (g.granteeType) {
        case 'project_role': {
          const roleKey = g.granteeId ? roleKeyById.get(g.granteeId) : undefined;
          return roleKey
            ? { permission: g.permission, granteeType: 'project_role', granteeRoleKey: roleKey }
            : undefined;
        }
        case 'tenant_role':
          return {
            permission: g.permission,
            granteeType: 'tenant_role',
            granteeTenantRole: g.granteeId as TemplatePermissionGrant['granteeTenantRole'],
          };
        case 'user':
        case 'group':
          return undefined;
        default:
          return { permission: g.permission, granteeType: g.granteeType };
      }
    })
    .filter((g) => g !== undefined);

  const viewDocs: TemplateView[] = byPosition(input.views).map((v) => ({
    name: v.name,
    kind: v.kind,
    query: exportQuery(v.query, refs),
    shared: v.shared,
    portalVisible: v.portalVisible,
    position: v.position,
  }));

  const slaDocs: TemplateSlaPolicy[] = byId(input.slaPolicies).map((s) => {
    const goals: TemplateSlaPolicy['goals'] = {};
    for (const [pid, goal] of Object.entries(s.goals)) {
      const name = priorityNameById.get(pid);
      if (name) {
        goals[name] = goal;
      }
    }
    const conditions = (list: ConditionRule[]) =>
      list.map((c) => exportCondition(c, refs)).filter((c) => c !== undefined);
    return {
      name: s.name,
      goals,
      startConditions: conditions(s.startConditions),
      pauseConditions: conditions(s.pauseConditions),
      stopConditions: conditions(s.stopConditions),
      calendar: s.calendar,
      enabled: s.enabled,
    };
  });

  const cannedDocs = byPosition(input.cannedResponses).map((c) => ({
    name: c.name,
    body: c.body,
    visibility: c.visibility,
    position: c.position,
  }));

  // Automations (S-07-11): straight copy, same caveat as materialize — no ref indirection,
  // so a rule using an id that only makes sense in this tenant (a `role`/`user` recipient, a
  // `webhook` subscription id, …) exports that id verbatim rather than a portable ref.
  const automationDocs: TemplateAutomationRule[] = byPosition(input.automations).map((a) => ({
    name: a.name,
    description: a.description,
    enabled: a.enabled,
    isSystem: a.isSystem,
    trigger: a.trigger,
    conditions: a.conditions,
    actions: a.actions,
    position: a.position,
  }));

  const project: ProjectTemplateDocument['project'] = {
    settings: ProjectSettingsSchema.parse(input.project.settings),
  };
  if (input.project.name !== null) {
    project.name = input.project.name;
  }
  if (input.project.category !== null) {
    project.category = input.project.category;
  }

  return {
    version: TEMPLATE_VERSION,
    project,
    priorities: priorityDocs,
    fields: fieldDocs,
    bindings: bindingDocs,
    issueTypes: issueTypeDocs,
    workflows: workflowDocs,
    forms: formDocs,
    roles: roleDocs,
    permissionGrants: grantDocs,
    views: viewDocs,
    slaPolicies: slaDocs,
    cannedResponses: cannedDocs,
    automations: automationDocs,
  };
}

/* ----------------------------- canonical JSON ----------------------------- */

/** Recursively sorts object keys and drops `undefined`; arrays keep their order. */
export function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    const record = value as Record<string, unknown>;
    for (const key of Object.keys(record).sort()) {
      if (record[key] !== undefined) {
        out[key] = canonicalize(record[key]);
      }
    }
    return out;
  }
  return value;
}

/** Deterministic JSON text; two exports of the same configuration are byte-identical. */
export const canonicalJson = (value: unknown): string => JSON.stringify(canonicalize(value));
